import React, { useEffect } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
    androidMenuFinishedLoading,
    iosMenuFinishedLoading,
} from 'flow/actions/embedded-app-actions';
import GeneralLink from 'flow/components/ui-components/GeneralLink';
import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import useNavigationMenu from 'flow/hooks/useNavigationMenu';
import useProfiles from 'flow/hooks/useProfiles';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import EmbeddedType from 'flow/types/embedded-type';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { NavigationConfiguration, NavigationItem } from 'flow/types/Navigation';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import {
    isTeamsAnyVersion,
    isTeamsFullManualLogin,
} from 'flow/utils/app-utils';
import { getCallRecordingTitle } from 'flow/utils/call-recording-monitoring-utils';
import { getIntegrationHost } from 'flow/utils/is-integration';
import { hasVaryingNotifications } from 'flow/utils/notification-utils';
import {
    callerIdVaries,
    callRecordingVaries,
    channelLoginVaries,
    incomingCallsVaries,
    monitoringVaries,
    personalCallRecordingVaries,
    queueLoginVaries,
} from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import { twMerge } from 'tailwind-merge';
import BrandLogo from './BrandLogo';
import { List } from 'immutable';
import { Profile } from 'flow/types/Profile';

const Nav = ({ label, icon, path, showHelp }: NavigationItem) => {
    const { i18next } = useAppContext();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    if (!path) {
        return (
            <div className="ms-2 mt-6 mb-2 text-xs font-bold text-gray-800">
                {i18next.t(label)}
            </div>
        );
    }

    return (
        <NavLink
            data-test-id={`settings-menu-${icon}`}
            to={generatePath(path, { extensionKey })}
            key={label}
            className="group block rounded-sm p-2 hover:bg-gray-200"
            activeClassName="bg-gray-200 active"
        >
            <div className="flex w-full items-center gap-x-3">
                <div
                    className={twMerge(
                        'flex size-10 items-center justify-center rounded-lg bg-gray-200',
                        'group-hover:bg-gray-300',
                        'group-[.active]:bg-gray-300',
                    )}
                >
                    <Icon
                        iconKey={icon}
                        size="24"
                        className={
                            ['callerid', 'incoming'].includes(icon) &&
                            'rtl:scale-x-[-1]'
                        }
                    />
                </div>
                <div>
                    <div className="leading-5">{i18next.t(label)}</div>
                    {showHelp?.() ? (
                        <p className="text-sm text-gray-500">
                            {i18next.t('settings.varies_per_profile')}
                        </p>
                    ) : null}
                </div>
            </div>
        </NavLink>
    );
};

const SettingsNavigation = () => {
    const { i18next, embedded } = useAppContext();
    const dispatch = useAppDispatch();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { extension } = useExtension(extensionKey);
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { config, adminPortalUrl, goToStatsInsights, logOut } =
        useNavigationMenu();
    const { profiles } = useProfiles(extensionKey);

    useEffect(() => {
        if (EmbeddedType.ANDROID.is(embedded)) {
            dispatch(androidMenuFinishedLoading());
        } else if (EmbeddedType.IOS.is(embedded)) {
            dispatch(iosMenuFinishedLoading());
        }
    }, [dispatch, embedded]);

    if (!extension) {
        return <Loading />;
    }

    const getNavigationForSetting = (
        profiles: List<TypedMap<Profile>>,
        extension: TypedMap<Extension>,
        config: NavigationConfiguration,
    ): NavigationItem[] => [
        {
            label: 'Settings.Account',
            icon: 'account',
            path: routes['settings.account'],
        },
        {
            label: 'settings.general',
            icon: 'general',
            path: routes['settings.general'],
        },
        {
            label: 'settings.softphone.telephony_and_audio',
            icon: 'softphone',
            path: routes['settings.softphone'],
        },
        {
            label: 'settings.notifications.device.mobile',
            icon: 'mobile',
            path: routes['settings.mobile'],
            enabled: config.shouldShowMobile,
            settingsOnBehalf: true,
        },
        {
            label: 'settings.voicemail',
            icon: 'voicemail',
            path: routes['settings.voicemail'],
            settingsOnBehalf: true,
        },
        {
            label: `${getIntegrationHost()} ${i18next.t('settings')}`,
            icon: 'extension',
            path: routes['settings.cti-settings'],
            enabled: config.shouldShowCti,
        },
        {
            label: 'settings.dashboards',
            icon: 'wallboard',
            path: routes['settings.dashboards'],
            enabled: config.shouldShowWallboard,
        },
        {
            label: 'settings.transcriptions',
            icon: 'article',
            path: routes['settings.transcriptions'],
            enabled: config.shouldShowTranscriptions,
        },
        {
            label: 'settings.could_edit',
            settingsOnBehalf: true,
        },
        {
            label: 'settings.notifications',
            icon: 'notifications',
            path: routes['settings.notifications'],
            showHelp: () => hasVaryingNotifications(extension.get('pushTypes')),
            settingsOnBehalf: true,
        },
        {
            label: 'settings.callerid',
            icon: 'callerid',
            path: routes['settings.callerid'],
            showHelp: () => callerIdVaries(profiles),
            settingsOnBehalf: true,
        },
        {
            label: 'settings.incoming_calls',
            icon: 'incoming',
            path: routes['settings.incoming-calls'],
            showHelp: () => incomingCallsVaries(profiles),
            settingsOnBehalf: true,
        },
        {
            label: getCallRecordingTitle(profiles.first()),
            icon: 'voicemailBox',
            path: routes['settings.call-recording-and-monitoring'],
            showHelp: () =>
                callRecordingVaries(profiles) ||
                personalCallRecordingVaries(profiles) ||
                monitoringVaries(profiles),
            settingsOnBehalf: true,
        },
        {
            label: 'settings.queue_channel_login',
            icon: 'queuelogin',
            path: routes['settings.queue-and-chat-login'],
            showHelp: () =>
                queueLoginVaries(profiles) || channelLoginVaries(profiles),
            settingsOnBehalf: true,
        },
        {
            label: 'settings.profile_settings',
            settingsOnBehalf: true,
        },
        {
            label: 'profilechooser.profiles',
            icon: 'profiles',
            path: routes['settings.profiles'],
            settingsOnBehalf: true,
        },
        {
            label: 'settings.workhours',
            icon: 'calendar',
            path: routes['settings.work-hours'],
            settingsOnBehalf: true,
        },
    ];

    return (
        <div
            className="scrollbar flex size-full flex-col gap-3 overflow-y-scroll border-e p-3"
            data-context-menu="false"
        >
            <BrandLogo type="logo24" className="p-2" />

            <div className="flex-1">
                {getNavigationForSetting(profiles, extension, config)
                    .filter((item) => item.enabled !== false)
                    .filter(
                        (item) =>
                            extensionKey === ownExtensionKey ||
                            item.settingsOnBehalf,
                    )
                    .map((item) => (
                        <Nav key={item.label} {...item} />
                    ))}
            </div>

            {/* In Teams only someone who signed in manually can sign out;
                with silent SSO there is nothing to sign out of. */}
            {(!isTeamsAnyVersion(embedded) ||
                isTeamsFullManualLogin(embedded)) &&
            extensionKey === ownExtensionKey ? (
                <div>
                    {!isTeamsAnyVersion(embedded) &&
                        config.shouldShowAdminPortal &&
                        adminPortalUrl && (
                            <GeneralLink
                                iconKey="openInNew"
                                href={adminPortalUrl}
                                className="bg-gray-100 px-3 hover:bg-gray-200"
                            >
                                {i18next.t('settings.to.flow')}
                            </GeneralLink>
                        )}

                    {!isTeamsAnyVersion(embedded) && config.shouldShowStats && (
                        <GeneralLink
                            iconKey="openInNew"
                            onClick={goToStatsInsights}
                            className="bg-gray-100 px-3 hover:bg-gray-200"
                        >
                            {i18next.t('settings.to.stats')}
                        </GeneralLink>
                    )}

                    {config.shouldShowLogOut && (
                        <GeneralLink
                            iconKey="logOut"
                            className="bg-gray-100 px-3 hover:bg-gray-200"
                            onClick={() => logOut('Settings')}
                        >
                            {i18next.t('app.signout')}
                        </GeneralLink>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default SettingsNavigation;
